import { useCallback } from "react";
import { useThemeStore } from "../theme";

interface IOSConfirmOptions {
  title?: string;
  content: string;
  okText?: string;
  cancelText?: string;
  showOk?: boolean;
  showCancel?: boolean;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  dangerouslySetInnerHTML?: boolean; // 支持 HTML 内容
}

/**
 * iOS 风格的确认对话框 Hook
 * 纯手动实现，配合自定义 CSS
 * 从 ConfigProvider 上下文读取 Ant Design 主题色
 */
export function useIOSConfirm() {
  const confirm = useCallback((options: IOSConfirmOptions) => {
    const {
      title = "確認",
      content,
      okText = "確認",
      cancelText = "取消",
      showOk = true,
      showCancel = true,
      onOk,
      onCancel,
      dangerouslySetInnerHTML = false,
    } = options;

    // 从主题 store 获取主题色（与 ConfigProvider 一致）
    const primaryColor = useThemeStore.getState().antdTheme.token?.colorPrimary || '#08c4c8';

    // 创建遮罩层
    const backdrop = document.createElement("div");
    backdrop.className = "dialog-backdrop";

    // 创建对话框
    const dialog = document.createElement("div");
    dialog.className = "dialog";

    // iPad 端视觉居中优化：补偿浏览器顶部UI（地址栏、工具栏）的高度
    const isIPad = (() => {
      const ua = navigator.userAgent;
      // 检测 iPad（包括 iPadOS 13+ 识别为桌面的情况）
      return /iPad/.test(ua) ||
             (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    })();

    // 浏览器顶部UI补偿：Safari约60-70px，Chrome约48-56px
    // 向上偏移35px使弹窗在整个屏幕上视觉更居中
    const offsetY = isIPad ? -35 : 0;

    // 创建对话框内容
    const inner = document.createElement("div");
    inner.className = "dialog-inner";

    if (title) {
      const titleEl = document.createElement("div");
      titleEl.className = "dialog-title";
      titleEl.textContent = title;
      inner.appendChild(titleEl);
    }

    const textEl = document.createElement("div");
    textEl.className = "dialog-text";
    if (dangerouslySetInnerHTML) {
      textEl.innerHTML = content;
    } else {
      textEl.textContent = content;
    }
    inner.appendChild(textEl);

    dialog.appendChild(inner);

    // 创建按钮容器
    const buttons = document.createElement("div");
    buttons.className = "dialog-buttons";

    // 取消按钮
    const cancelBtn = document.createElement("button");
    cancelBtn.className = "dialog-button";
    cancelBtn.textContent = cancelText;
    cancelBtn.style.color = primaryColor; // 应用主题色
    cancelBtn.onclick = () => {
      closeDialog();
      onCancel?.();
    };

    // 确认按钮
    const okBtn = document.createElement("button");
    okBtn.className = "dialog-button dialog-button-bold";
    okBtn.textContent = okText;
    okBtn.style.color = primaryColor; // 应用主题色
    okBtn.onclick = async () => {
      closeDialog();
      if (onOk) {
        const result = onOk();
        if (result instanceof Promise) {
          await result;
        }
      }
    };

    if (showCancel) {
      buttons.appendChild(cancelBtn);
    }
    if (showOk) {
      buttons.appendChild(okBtn);
    }
    dialog.appendChild(buttons);

    backdrop.appendChild(dialog);

    // 关闭对话框函数
    const closeDialog = () => {
      backdrop.classList.remove("modal-in");
      setTimeout(() => {
        if (document.body.contains(backdrop)) {
          document.body.removeChild(backdrop);
        }
      }, 300);
    };

    // 点击遮罩层关闭（可选）
    backdrop.onclick = (e) => {
      if (e.target === backdrop) {
        closeDialog();
        onCancel?.();
      }
    };

    // 添加到 body
    document.body.appendChild(backdrop);

    // 设置初始动画状态（放大）
    dialog.style.transform = `scale(1.15) translateY(${offsetY}px)`;

    // 触发动画（缩放到正常大小，保持 translateY）
    requestAnimationFrame(() => {
      backdrop.classList.add("modal-in");
      dialog.style.transform = `scale(1) translateY(${offsetY}px)`;
    });
  }, []);

  return { confirm };
}
